import { getClientCookie } from "@lib/cookies.client";

interface UTMParams {
  [key: string]: string;
}

interface LeadData {
  [key: string]: any;
}

// Constants
const UTM_PARAMS_KEYS = [
  "utm_source",
  "utm_medium", 
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_device",
  "utm_adgroup",
  "utm_Sitelink",
  "utm_location",
] as const;

const STORAGE_KEYS = {
  UTM_PARAMS: "utm_params",
  UTM_EXPIRY_DATE: "utm_expiry_date",
} as const;

const UTM_EXPIRY_HOURS = 1;

/**
 * Safely parses JSON with error handling
 */
function safeJsonParse<T>(jsonString: string | null, fallback: T): T {
  if (!jsonString) return fallback;
  
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.warn("Failed to parse JSON:", jsonString, error);
    return fallback;
  }
}

/**
 * Extracts UTM parameters from URL query string
 */
function extractUTMParamsFromURL(): UTMParams {
  const queryDict: UTMParams = {};
  
  try {
    // Use URLSearchParams for more robust query parsing
    const urlParams = new URLSearchParams(location.search);
    
    UTM_PARAMS_KEYS.forEach((key) => {
      const value = urlParams.get(key);
      if (value !== null) {
        queryDict[key] = value;
      }
    });
  } catch (error) {
    console.error("Error parsing URL parameters:", error);
  }
  
  return queryDict;
}

/**
 * Checks if UTM parameters have expired
 */
function isUTMParamsExpired(): boolean {
  const expiryDateStr = sessionStorage.getItem(STORAGE_KEYS.UTM_EXPIRY_DATE);
  if (!expiryDateStr) return false;
  
  try {
    const expiryDate = new Date(expiryDateStr);
    return new Date() > expiryDate;
  } catch (error) {
    console.warn("Error parsing expiry date:", error);
    return true; // If we can't parse the date, consider it expired
  }
}

/**
 * Clears expired UTM parameters from session storage
 */
function clearExpiredUTMParams(): void {
  if (isUTMParamsExpired()) {
    sessionStorage.removeItem(STORAGE_KEYS.UTM_EXPIRY_DATE);
    sessionStorage.removeItem(STORAGE_KEYS.UTM_PARAMS);
  }
}

/**
 * Merges new UTM parameters with existing ones
 */
function mergeUTMParams(newParams: UTMParams): UTMParams {
  const existingParams = safeJsonParse<UTMParams>(
    sessionStorage.getItem(STORAGE_KEYS.UTM_PARAMS),
    {}
  );
  
  return {
    ...existingParams,
    ...newParams,
  };
}

/**
 * Stores UTM parameters in session storage with expiry
 */
function storeUTMParams(params: UTMParams): void {
  const expiryDate = new Date();
  expiryDate.setHours(expiryDate.getHours() + UTM_EXPIRY_HOURS);
  
  sessionStorage.setItem(STORAGE_KEYS.UTM_PARAMS, JSON.stringify(params));
  sessionStorage.setItem(STORAGE_KEYS.UTM_EXPIRY_DATE, JSON.stringify(expiryDate));
}

export function getUTMParams(getDict = false): UTMParams | void {
  // Clear expired params first
  clearExpiredUTMParams();
  
  // Extract current UTM params from URL
  const currentParams = extractUTMParamsFromURL();
  
  // If we have new params, update storage
  if (Object.keys(currentParams).length > 0) {
    const mergedParams = mergeUTMParams(currentParams);
    storeUTMParams(mergedParams);
  }
  
  if (getDict) {
    return Object.keys(currentParams).length > 0 
      ? currentParams 
      : safeJsonParse<UTMParams>(sessionStorage.getItem(STORAGE_KEYS.UTM_PARAMS), {});
  }
}

export function addUTMParamsToLeadData(leadData: LeadData): LeadData {
  try {
    // Clear expired params first
    clearExpiredUTMParams();
    
    // Try to get UTM params from cookie first
    const utmParamsCookie = getClientCookie("utm_params");
    if (utmParamsCookie) {
      const cookieParams = safeJsonParse<UTMParams>(utmParamsCookie, {});
      Object.assign(leadData, cookieParams);
      return leadData;
    }
    
    // Fallback to session storage
    const sessionParams = safeJsonParse<UTMParams>(
      sessionStorage.getItem(STORAGE_KEYS.UTM_PARAMS),
      {}
    );
    
    if (Object.keys(sessionParams).length > 0) {
      Object.assign(leadData, sessionParams);
      return leadData;
    }
    
    // Last resort: extract from current URL
    const currentParams = extractUTMParamsFromURL();
    if (Object.keys(currentParams).length > 0) {
      Object.assign(leadData, currentParams);
    }
  } catch (error) {
    console.error("Error adding UTM params to lead data:", error);
  }

  return leadData;
}

export const cityPopupUTMCampaign = [
  "storefront_Drive_app_engagement_activeCX",
] as const;
